/**
 * Furnace recipes (vanilla smelting, restricted to what this engine has) and
 * fuel lookup. DOM-free.
 */

import * as B from '../blocks';
import { I } from './itemIds';
import { itemDef } from './itemdb';
import { stackOf, type Stack } from './types';

/** Ticks to cook one item. */
export const SMELT_TICKS = 200;
/** Blast furnace (ores) and smoker (food) cook twice as fast; those blocks are job sites only for now (no cooking UI), so nothing consumes this yet. */
export const FAST_SMELT_TICKS = SMELT_TICKS / 2;

const named = (n: string): number => B.BLOCKS.find((b) => b?.name === n)?.id ?? -1;

const R = new Map<number, [number, number]>();
function add(input: number, out: number, count = 1): void {
  if (input > 0 && out > 0) R.set(input, [out, count]);
}

// raw ores & ore blocks -> ingots / gems / dust
for (const id of [B.IRON_ID, B.DEEPSLATE_IRON_ORE_ID, I.RAW_IRON]) add(id, I.IRON_INGOT);
for (const id of [B.GOLD_ID, B.DEEPSLATE_GOLD_ORE_ID, I.RAW_GOLD]) add(id, I.GOLD_INGOT);
for (const id of [B.COPPER_ORE_ID, B.DEEPSLATE_COPPER_ORE_ID, I.RAW_COPPER]) add(id, I.COPPER_INGOT);
for (const id of [B.COAL_ID, B.DEEPSLATE_COAL_ORE_ID]) add(id, I.COAL);
for (const id of [B.DIAMOND_ID, B.DEEPSLATE_DIAMOND_ORE_ID]) add(id, I.DIAMOND);
for (const id of [B.EMERALD_ORE_ID, B.DEEPSLATE_EMERALD_ORE_ID]) add(id, I.EMERALD);
for (const id of [B.LAPIS_ORE_ID, B.DEEPSLATE_LAPIS_ORE_ID]) add(id, I.LAPIS_LAZULI);
for (const id of [B.REDSTONE_ORE_ID, B.DEEPSLATE_REDSTONE_ORE_ID]) add(id, I.REDSTONE, 1);
// blocks
add(B.COBBLE_ID, B.STONE_ID);
add(B.COBBLED_DEEPSLATE_ID, B.DEEPSLATE_ID);
add(B.SAND_ID, B.GLASS_ID);
add(B.RED_SAND_ID, B.GLASS_ID);
add(B.CLAY_ID, B.TERRACOTTA_ID);
add(B.STONE_ID, B.SMOOTH_STONE_ID);
add(B.STONE_BRICK_ID, B.CRACKED_BRICKS_ID);
add(B.SANDSTONE_ID, named('Smooth Sandstone'));
add(B.RED_SANDSTONE_ID, named('Smooth Red Sandstone'));
add(named('Deepslate Bricks'), named('Cracked Deepslate Bricks'));
add(named('Deepslate Tiles'), named('Cracked Deepslate Tiles'));
add(B.BASALT_ID, B.SMOOTH_BASALT_ID);
add(B.NETHER_BRICKS_ID, named('Cracked Nether Bricks'));
// items
add(I.CLAY_BALL, I.BRICK);
add(I.BEEF, I.COOKED_BEEF);
add(I.COD, I.COOKED_COD);
add(I.SALMON, I.COOKED_SALMON);
add(I.PORKCHOP, I.COOKED_PORKCHOP);
add(I.CHICKEN, I.COOKED_CHICKEN);
add(I.MUTTON, I.COOKED_MUTTON);
add(I.RABBIT, I.COOKED_RABBIT);
add(I.POTATO, I.BAKED_POTATO);
// logs -> charcoal
for (const id of [B.LOG_ID, B.SPRUCE_LOG_ID, B.BIRCH_LOG_ID, B.JUNGLE_LOG_ID, B.ACACIA_LOG_ID,
  B.DARK_OAK_LOG_ID, B.MANGROVE_LOG_ID, B.CHERRY_LOG_ID, B.PALE_OAK_LOG_ID]) add(id, I.CHARCOAL);

// nether
add(B.ANCIENT_DEBRIS_ID, I.NETHERITE_SCRAP);
add(B.NETHERRACK_ID, I.NETHER_BRICK);
add(B.QUARTZ_ORE_ID, I.QUARTZ);
add(B.NETHER_GOLD_ORE_ID, I.GOLD_INGOT);
add(B.NETHER_BRICKS_ID, B.CRACKED_NETHER_BRICKS_ID);
add(I.CHORUS_FRUIT, I.POPPED_CHORUS_FRUIT);

/** Experience stored per smelted item, by output (vanilla values); anything else gives 0.1. */
const XP = new Map<number, number>([
  [I.IRON_INGOT, 0.7], [I.GOLD_INGOT, 1], [I.COPPER_INGOT, 0.7], [I.COAL, 0.1], [I.DIAMOND, 1], [I.EMERALD, 1],
  [I.LAPIS_LAZULI, 0.2], [I.REDSTONE, 0.7], [I.QUARTZ, 0.2], [I.NETHERITE_SCRAP, 2], [I.BRICK, 0.3],
  [I.CHARCOAL, 0.15], [B.TERRACOTTA_ID, 0.35],
  [I.COOKED_BEEF, 0.35], [I.COOKED_COD, 0.35], [I.COOKED_SALMON, 0.35], [I.COOKED_PORKCHOP, 0.35],
  [I.COOKED_CHICKEN, 0.35], [I.COOKED_MUTTON, 0.35], [I.BAKED_POTATO, 0.35],
]);

/** Experience one smelt of `id` stores in the furnace until the output is taken. */
export function smeltXp(id: number): number {
  const r = R.get(id);
  return r ? XP.get(r[0]) ?? 0.1 : 0;
}

/** Every [input id, output stack] pair (read-only list for the recipe browser). */
export function smeltRecipes(): Array<[number, Stack]> {
  return [...R].map(([input, [out, count]]) => [input, stackOf(out, count, 0)]);
}

/** The stack one input item smelts into (a fresh Stack), or null when it does not smelt. */
export function smeltResult(id: number): Stack | null {
  const r = R.get(id);
  return r ? stackOf(r[0], r[1], 0) : null;
}

/** Furnace burn time in ticks of a fuel item/block (0 = not a fuel). */
export function fuelTicks(id: number): number {
  return id > 0 ? itemDef(id).fuel : 0;
}
